/**
 * Main JavaScript file for GratXRay theme
 *
 * @package GratXRay
 */

(function() {
    'use strict';

    /**
     * Set header height as CSS variable for hero section calculations
     * Also accounts for WordPress admin bar when logged in
     */
    function setHeaderHeight() {
        const header = document.getElementById('masthead');
        const adminBar = document.getElementById('wpadminbar');
        
        if (header) {
            const headerHeight = header.offsetHeight;
            const adminBarHeight = adminBar ? adminBar.offsetHeight : 0;
            const totalOffset = headerHeight + adminBarHeight;
            
            document.documentElement.style.setProperty('--header-height', headerHeight + 'px');
            document.documentElement.style.setProperty('--admin-bar-height', adminBarHeight + 'px');
            document.documentElement.style.setProperty('--total-header-offset', totalOffset + 'px');
        }
    }

    // Set header height on load
    setHeaderHeight();

    // Update on window resize
    let resizeTimer;
    window.addEventListener('resize', function() {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(setHeaderHeight, 100);
    });

    /**
     * Smart Sticky Header - Hide on scroll down, show on scroll up
     * Also reduces padding when scrolled
     */
    function initStickyHeader() {
        const header = document.getElementById('masthead');
        if (!header) return;

        let lastScrollTop = 0;
        let scrollThreshold = 100; // Start hiding after scrolling 100px
        let ticking = false;
        let wasScrolled = false;

        function updateHeader(scrollTop) {
            // Add scrolled class when user has scrolled down
            const isScrolled = scrollTop > 50;
            
            if (isScrolled !== wasScrolled) {
                if (isScrolled) {
                    header.classList.add('header-scrolled');
                } else {
                    header.classList.remove('header-scrolled');
                }
                
                wasScrolled = isScrolled;
            }

            // At the very top of the page, always show header
            if (scrollTop < scrollThreshold) {
                header.style.transform = 'translateY(0)';
                header.classList.remove('header-hidden');
                return;
            }

            // Scrolling down - hide header
            if (scrollTop > lastScrollTop && scrollTop > scrollThreshold) {
                header.style.transform = 'translateY(-100%)';
                header.classList.add('header-hidden');
            } 
            // Scrolling up - show header
            else if (scrollTop < lastScrollTop) {
                header.style.transform = 'translateY(0)';
                header.classList.remove('header-hidden');
            }

            lastScrollTop = scrollTop;
        }

        function onScroll() {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

            if (!ticking) {
                window.requestAnimationFrame(function() {
                    updateHeader(scrollTop);
                    ticking = false;
                });
                ticking = true;
            }
        }

        // Listen to scroll events
        window.addEventListener('scroll', onScroll, { passive: true });
    }

    // Initialize sticky header
    initStickyHeader();

    /**
     * Set header background color to match design when menu is open
     */
    function setHeaderBackground() {
        const header = document.getElementById('masthead');
        if (header) {
            // Add transition if not already set - match menu fade speed
            if (!header.style.transition) {
                header.style.transition = 'background-color 300ms';
            }
            
            // Check if menu is open
            const isMenuOpen = menuToggle && menuToggle.getAttribute('aria-expanded') === 'true';
            if (isMenuOpen) {
                header.style.backgroundColor = '#DFEDF2'; // blue 03-bg from Figma
            } else {
                header.style.backgroundColor = '#ffffff'; // white when closed
            }
        }
    }

    // Full-screen menu overlay toggle
    const menuToggle = document.querySelector('.menu-toggle');
    const navigation = document.querySelector('.main-navigation');
    const body = document.body;
    const hamburgerIcon = document.querySelector('.menu-icon-hamburger');
    const closeIcon = document.querySelector('.menu-icon-close');

    /**
     * Open the navigation menu (full-screen overlay)
     */
    function openMenu() {
        if (!navigation) return;

        navigation.classList.remove('opacity-0', 'pointer-events-none');
        navigation.classList.add('opacity-100', 'pointer-events-auto');

        // Toggle animated icon
        const animatedIcon = menuToggle ? menuToggle.querySelector('.nc-int-menu-to-close') : null;
        if (animatedIcon) {
            animatedIcon.classList.add('nc-int-icon-state-b');
        }

        if (menuToggle) {
            menuToggle.setAttribute('aria-expanded', 'true');
        }
        
        // Prevent body scroll
        body.style.overflow = 'hidden';
        
        // Update header background
        setHeaderBackground();
    }

    /**
     * Close the navigation menu (full-screen overlay)
     */
    function closeMenu() {
        if (!navigation) return;

        navigation.classList.add('opacity-0', 'pointer-events-none');
        navigation.classList.remove('opacity-100', 'pointer-events-auto');

        // Toggle animated icon
        const animatedIcon = menuToggle ? menuToggle.querySelector('.nc-int-menu-to-close') : null;
        if (animatedIcon) {
            animatedIcon.classList.remove('nc-int-icon-state-b');
        }

        if (menuToggle) {
            menuToggle.setAttribute('aria-expanded', 'false');
        }
        
        // Restore body scroll
        body.style.overflow = '';
        
        // Update header background
        setHeaderBackground();
    }

    // Toggle menu when button is clicked
    if (menuToggle) {
        menuToggle.addEventListener('click', function(e) {
            e.preventDefault();
            const isOpen = menuToggle.getAttribute('aria-expanded') === 'true';
            if (isOpen) {
                closeMenu();
            } else {
                openMenu();
            }
        });
    }

    // Close menu when pressing Escape key
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && menuToggle && menuToggle.getAttribute('aria-expanded') === 'true') {
            closeMenu();
        }
    });

    // Close menu when clicking on any menu link
    if (navigation) {
        const menuLinks = navigation.querySelectorAll('a');
        menuLinks.forEach(function(link) {
            link.addEventListener('click', function() {
                closeMenu();
            });
        });
    }

    /**
     * Language Selector Dropdown functionality
     */
    function initLanguageSelectors() {
        const languageSelectors = document.querySelectorAll('[data-language-selector]');
        
        languageSelectors.forEach(function(selector) {
            const toggle = selector.querySelector('.language-selector-toggle');
            const dropdown = selector.querySelector('.language-selector-dropdown');
            const chevron = selector.querySelector('.language-chevron');
            
            if (!toggle || !dropdown) return;
            
            // Toggle dropdown on button click
            toggle.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                
                const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
                
                // Close all other language dropdowns first
                document.querySelectorAll('[data-language-selector]').forEach(function(otherSelector) {
                    if (otherSelector !== selector) {
                        const otherToggle = otherSelector.querySelector('.language-selector-toggle');
                        const otherDropdown = otherSelector.querySelector('.language-selector-dropdown');
                        const otherChevron = otherSelector.querySelector('.language-chevron');
                        
                        if (otherToggle && otherDropdown) {
                            otherToggle.setAttribute('aria-expanded', 'false');
                            otherDropdown.classList.add('hidden');
                            if (otherChevron) {
                                otherChevron.style.transform = 'rotate(0deg)';
                            }
                        }
                    }
                });
                
                // Toggle current dropdown
                if (isExpanded) {
                    toggle.setAttribute('aria-expanded', 'false');
                    dropdown.classList.add('hidden');
                    if (chevron) {
                        chevron.style.transform = 'rotate(0deg)';
                    }
                } else {
                    toggle.setAttribute('aria-expanded', 'true');
                    dropdown.classList.remove('hidden');
                    if (chevron) {
                        chevron.style.transform = 'rotate(180deg)';
                    }
                }
            });
        });
        
        // Close dropdowns when clicking outside
        document.addEventListener('click', function(e) {
            languageSelectors.forEach(function(selector) {
                const toggle = selector.querySelector('.language-selector-toggle');
                const dropdown = selector.querySelector('.language-selector-dropdown');
                const chevron = selector.querySelector('.language-chevron');
                
                if (!selector.contains(e.target) && toggle && dropdown) {
                    toggle.setAttribute('aria-expanded', 'false');
                    dropdown.classList.add('hidden');
                    if (chevron) {
                        chevron.style.transform = 'rotate(0deg)';
                    }
                }
            });
        });
        
        // Close dropdowns on Escape key
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape') {
                languageSelectors.forEach(function(selector) {
                    const toggle = selector.querySelector('.language-selector-toggle');
                    const dropdown = selector.querySelector('.language-selector-dropdown');
                    const chevron = selector.querySelector('.language-chevron');
                    
                    if (toggle && dropdown && toggle.getAttribute('aria-expanded') === 'true') {
                        toggle.setAttribute('aria-expanded', 'false');
                        dropdown.classList.add('hidden');
                        if (chevron) {
                            chevron.style.transform = 'rotate(0deg)';
                        }
                    }
                });
            }
        });
    }
    
    // Initialize language selectors on page load
    initLanguageSelectors();

    /**
     * Accordion functionality
     */
    const accordionTriggers = document.querySelectorAll('.accordion-trigger');

    accordionTriggers.forEach(function(trigger) {
        trigger.addEventListener('click', function() {
            const content = document.getElementById(this.getAttribute('aria-controls'));
            const icon = this.querySelector('.accordion-icon');
            const isExpanded = this.getAttribute('aria-expanded') === 'true';

            // Toggle current item
            this.setAttribute('aria-expanded', !isExpanded);
            content.classList.toggle('hidden');
            if (icon) {
                icon.style.transform = isExpanded ? '' : 'rotate(180deg)';
            }
        });
    });

    /**
     * Testimonial Slider functionality with Flickity
     */
    if (typeof Flickity !== 'undefined') {
        const testimonialSliders = document.querySelectorAll('.testimonial-slider');
        
        testimonialSliders.forEach(function(slider) {
            // Initialize Flickity - no looping, no data attribute
            const flkty = new Flickity(slider, {
                cellAlign: 'left',
                contain: true,
                prevNextButtons: false,
                pageDots: false,
                wrapAround: false,
                adaptiveHeight: false,
                draggable: true
            });
            
            // Get navigation buttons and author info container
            const prevBtn = slider.parentElement.querySelector('.testimonial-prev');
            const nextBtn = slider.parentElement.querySelector('.testimonial-next');
            const authorInfo = slider.closest('.testimonial-block').querySelector('.testimonial-author-info');
            
            // Function to update author info from current slide
            function updateAuthorInfo() {
                if (!authorInfo) return;
                
                const currentSlide = flkty.selectedElement;
                if (!currentSlide) return;
                
                const authorName = currentSlide.getAttribute('data-author-name');
                const authorOrg = currentSlide.getAttribute('data-author-org');
                
                let html = '';
                if (authorName) {
                    html += '<p class="font-normal mb-0 text-xl sm:text-2xl md:text-3xl lg:text-4xl leading-tight">' + authorName + '</p>';
                }
                if (authorOrg) {
                    html += '<p class="font-normal text-sm sm:text-base md:text-lg lg:text-xl leading-normal">' + authorOrg + '</p>';
                }
                
                authorInfo.innerHTML = html;
            }
            
            // Function to update button states
            function updateButtonStates() {
                const currentIndex = flkty.selectedIndex;
                const totalSlides = flkty.slides.length;
                
                // Update prev button state (disable on first slide)
                if (prevBtn) {
                    const prevPath = prevBtn.querySelector('path');
                    if (currentIndex === 0) {
                        prevBtn.style.cursor = 'not-allowed';
                        prevBtn.disabled = true;
                        if (prevPath) {
                            prevPath.setAttribute('fill', '#30344B');
                            prevPath.setAttribute('fill-opacity', '0.3');
                        }
                    } else {
                        prevBtn.style.cursor = 'pointer';
                        prevBtn.disabled = false;
                        if (prevPath) {
                            prevPath.setAttribute('fill', '#30344B');
                            prevPath.setAttribute('fill-opacity', '1');
                        }
                    }
                }
                
                // Update next button state (disable on last slide)
                if (nextBtn) {
                    const nextPath = nextBtn.querySelector('path');
                    if (currentIndex === totalSlides - 1) {
                        nextBtn.style.cursor = 'not-allowed';
                        nextBtn.disabled = true;
                        if (nextPath) {
                            nextPath.setAttribute('fill', '#30344B');
                            nextPath.setAttribute('fill-opacity', '0.3');
                        }
                    } else {
                        nextBtn.style.cursor = 'pointer';
                        nextBtn.disabled = false;
                        if (nextPath) {
                            nextPath.setAttribute('fill', '#30344B');
                            nextPath.setAttribute('fill-opacity', '1');
                        }
                    }
                }
            }
            
            // Custom navigation button click handlers
            if (prevBtn) {
                prevBtn.addEventListener('click', function(e) {
                    e.preventDefault();
                    if (!this.disabled) {
                        flkty.previous();
                    }
                });
            }
            
            if (nextBtn) {
                nextBtn.addEventListener('click', function(e) {
                    e.preventDefault();
                    if (!this.disabled) {
                        flkty.next();
                    }
                });
            }
            
            // Update on slide change
            flkty.on('change', function() {
                updateButtonStates();
                updateAuthorInfo();
            });
            
            // Initial state
            updateButtonStates();
            updateAuthorInfo();
        });
    }

    /**
     * Team Grid Filter functionality
     */
    const teamFilters = document.querySelectorAll('.team-filter');

    teamFilters.forEach(function(filterContainer) {
        const filterButtons = filterContainer.querySelectorAll('.filter-btn');
        const teamGrid = filterContainer.closest('.team-grid-block').querySelector('.team-grid');
        const teamMembers = teamGrid ? teamGrid.querySelectorAll('.team-member') : [];

        filterButtons.forEach(function(button) {
            button.addEventListener('click', function() {
                const filter = this.getAttribute('data-filter');

                // Update active button
                filterButtons.forEach(function(btn) {
                    btn.classList.remove('active', 'border-black', 'bg-black', 'text-white');
                    btn.classList.add('border-gray-border', 'text-gray-muted');
                });
                this.classList.remove('border-gray-border', 'text-gray-muted');
                this.classList.add('active', 'border-black');

                // Filter team members
                teamMembers.forEach(function(member) {
                    const category = member.getAttribute('data-category');

                    if (filter === 'all' || category === filter) {
                        member.classList.remove('hidden');
                        // Fade in animation
                        member.style.animation = 'fadeIn 0.3s ease-in';
                    } else {
                        member.classList.add('hidden');
                    }
                });
            });
        });
    });

    /**
     * Team Slider functionality with Flickity
     */
    if (typeof Flickity !== 'undefined') {
        const teamSliders = document.querySelectorAll('.team-slider');
        
        teamSliders.forEach(function(slider) {
            // Initialize Flickity - no looping
            const flkty = new Flickity(slider, {
                cellAlign: 'left',
                contain: true,
                prevNextButtons: false,
                pageDots: false,
                wrapAround: false,
                groupCells: true,
                draggable: true
            });
            
            // Get navigation buttons
            const prevBtn = slider.closest('.team-grid-block').querySelector('.team-slider-prev');
            const nextBtn = slider.closest('.team-grid-block').querySelector('.team-slider-next');
            
            // Function to update button states
            function updateButtonStates() {
                const currentIndex = flkty.selectedIndex;
                const totalSlides = flkty.slides.length;
                
                // Update prev button state (disable on first slide)
                if (prevBtn) {
                    const prevPath = prevBtn.querySelector('path');
                    if (currentIndex === 0) {
                        prevBtn.style.cursor = 'not-allowed';
                        prevBtn.disabled = true;
                        if (prevPath) {
                            prevPath.setAttribute('fill', '#30344B');
                            prevPath.setAttribute('fill-opacity', '0.3');
                        }
                    } else {
                        prevBtn.style.cursor = 'pointer';
                        prevBtn.disabled = false;
                        if (prevPath) {
                            prevPath.setAttribute('fill', '#30344B');
                            prevPath.setAttribute('fill-opacity', '1');
                        }
                    }
                }
                
                // Update next button state (disable on last slide)
                if (nextBtn) {
                    const nextPath = nextBtn.querySelector('path');
                    if (currentIndex === totalSlides - 1) {
                        nextBtn.style.cursor = 'not-allowed';
                        nextBtn.disabled = true;
                        if (nextPath) {
                            nextPath.setAttribute('fill', '#30344B');
                            nextPath.setAttribute('fill-opacity', '0.3');
                        }
                    } else {
                        nextBtn.style.cursor = 'pointer';
                        nextBtn.disabled = false;
                        if (nextPath) {
                            nextPath.setAttribute('fill', '#30344B');
                            nextPath.setAttribute('fill-opacity', '1');
                        }
                    }
                }
            }
            
            // Custom navigation button click handlers
            if (prevBtn) {
                prevBtn.addEventListener('click', function(e) {
                    e.preventDefault();
                    if (!this.disabled) {
                        flkty.previous();
                    }
                });
            }
            
            if (nextBtn) {
                nextBtn.addEventListener('click', function(e) {
                    e.preventDefault();
                    if (!this.disabled) {
                        flkty.next();
                    }
                });
            }
            
            // Update on slide change
            flkty.on('change', updateButtonStates);
            
            // Initial state
            updateButtonStates();
        });
    } else {
        // Fallback if Flickity is not loaded
        const teamSliders = document.querySelectorAll('.team-slider');
        teamSliders.forEach(function(slider) {
            console.warn('Flickity not loaded for team slider');
        });
    }

    /**
     * Partner Logos Slider functionality with Flickity
     */
    if (typeof Flickity !== 'undefined') {
        const partnerLogosSliders = document.querySelectorAll('.partner-logos-slider');
        
        partnerLogosSliders.forEach(function(slider) {
            const isGlobalSlider = slider.classList.contains('global-partners-slider');
            const logoCount = parseInt(slider.getAttribute('data-logo-count') || '0');
            
            if (isGlobalSlider) {
                // Global partners slider - use groupCells like other sliders
                const flkty = new Flickity(slider, {
                    cellAlign: 'left',
                    contain: true,
                    prevNextButtons: false,
                    pageDots: false,
                    wrapAround: false,
                    groupCells: true,
                    draggable: true
                });
                
                // Get navigation buttons
                const prevBtn = slider.closest('.partner-logos-block').querySelector('.partner-logos-prev');
                const nextBtn = slider.closest('.partner-logos-block').querySelector('.partner-logos-next');
                const navContainer = slider.closest('.partner-logos-block').querySelector('.partner-logos-nav');
                
                // Function to check if navigation is needed
                function checkNavigationNeeded() {
                    const totalSlides = flkty.slides.length;
                    
                    // Hide navigation if only one slide (all items visible)
                    if (navContainer) {
                        if (totalSlides <= 1) {
                            navContainer.style.display = 'none';
                        } else {
                            navContainer.style.display = 'flex';
                        }
                    }
                }
                
                // Function to update button states - same logic as other sliders
                function updateButtonStates() {
                    const currentIndex = flkty.selectedIndex;
                    const totalSlides = flkty.slides.length;
                    
                    // Update prev button state (disable on first slide)
                    if (prevBtn) {
                        const prevPath = prevBtn.querySelector('path');
                        if (currentIndex === 0) {
                            prevBtn.style.cursor = 'not-allowed';
                            prevBtn.disabled = true;
                            if (prevPath) {
                                prevPath.setAttribute('fill', '#30344B');
                                prevPath.setAttribute('fill-opacity', '0.3');
                            }
                        } else {
                            prevBtn.style.cursor = 'pointer';
                            prevBtn.disabled = false;
                            if (prevPath) {
                                prevPath.setAttribute('fill', '#30344B');
                                prevPath.setAttribute('fill-opacity', '1');
                            }
                        }
                    }
                    
                    // Update next button state (disable on last slide)
                    if (nextBtn) {
                        const nextPath = nextBtn.querySelector('path');
                        if (currentIndex === totalSlides - 1) {
                            nextBtn.style.cursor = 'not-allowed';
                            nextBtn.disabled = true;
                            if (nextPath) {
                                nextPath.setAttribute('fill', '#30344B');
                                nextPath.setAttribute('fill-opacity', '0.3');
                            }
                        } else {
                            nextBtn.style.cursor = 'pointer';
                            nextBtn.disabled = false;
                            if (nextPath) {
                                nextPath.setAttribute('fill', '#30344B');
                                nextPath.setAttribute('fill-opacity', '1');
                            }
                        }
                    }
                }
                
                // Custom navigation button click handlers
                if (prevBtn) {
                    prevBtn.addEventListener('click', function(e) {
                        e.preventDefault();
                        if (!this.disabled) {
                            flkty.previous();
                        }
                    });
                }
                
                if (nextBtn) {
                    nextBtn.addEventListener('click', function(e) {
                        e.preventDefault();
                        if (!this.disabled) {
                            flkty.next();
                        }
                    });
                }
                
                // Update on slide change
                flkty.on('change', updateButtonStates);
                
                // Check on resize
                flkty.on('resize', checkNavigationNeeded);
                
                // Initial state
                checkNavigationNeeded();
                updateButtonStates();
                
            } else {
                // Old partner logos slider (flexible content block) - use groupCells like other sliders
                const flkty = new Flickity(slider, {
                    cellAlign: 'left',
                    contain: true,
                    prevNextButtons: false,
                    pageDots: false,
                    wrapAround: false,
                    groupCells: true,
                    draggable: true
                });
                
                // Get navigation buttons
                const prevBtn = slider.closest('.partner-logos-block').querySelector('.partner-logos-prev');
                const nextBtn = slider.closest('.partner-logos-block').querySelector('.partner-logos-next');
                const navContainer = slider.closest('.partner-logos-block').querySelector('.partner-logos-nav');
                
                // Function to check if navigation is needed
                function checkNavigationNeeded() {
                    const totalSlides = flkty.slides.length;
                    
                    // Hide navigation if only one slide (all items visible)
                    if (navContainer) {
                        if (totalSlides <= 1) {
                            navContainer.style.display = 'none';
                        } else {
                            navContainer.style.display = 'flex';
                        }
                    }
                }
                
                // Function to update button states - same logic as other sliders
                function updateButtonStates() {
                    const currentIndex = flkty.selectedIndex;
                    const totalSlides = flkty.slides.length;
                    
                    // Update prev button state (disable on first slide)
                    if (prevBtn) {
                        const prevPath = prevBtn.querySelector('path');
                        if (currentIndex === 0) {
                            prevBtn.style.cursor = 'not-allowed';
                            prevBtn.disabled = true;
                            if (prevPath) {
                                prevPath.setAttribute('fill', '#30344B');
                                prevPath.setAttribute('fill-opacity', '0.3');
                            }
                        } else {
                            prevBtn.style.cursor = 'pointer';
                            prevBtn.disabled = false;
                            if (prevPath) {
                                prevPath.setAttribute('fill', '#30344B');
                                prevPath.setAttribute('fill-opacity', '1');
                            }
                        }
                    }
                    
                    // Update next button state (disable on last slide)
                    if (nextBtn) {
                        const nextPath = nextBtn.querySelector('path');
                        if (currentIndex === totalSlides - 1) {
                            nextBtn.style.cursor = 'not-allowed';
                            nextBtn.disabled = true;
                            if (nextPath) {
                                nextPath.setAttribute('fill', '#30344B');
                                nextPath.setAttribute('fill-opacity', '0.3');
                            }
                        } else {
                            nextBtn.style.cursor = 'pointer';
                            nextBtn.disabled = false;
                            if (nextPath) {
                                nextPath.setAttribute('fill', '#30344B');
                                nextPath.setAttribute('fill-opacity', '1');
                            }
                        }
                    }
                }
                
                if (prevBtn) {
                    prevBtn.addEventListener('click', function(e) {
                        e.preventDefault();
                        if (!this.disabled) {
                            flkty.previous();
                        }
                    });
                }
                
                if (nextBtn) {
                    nextBtn.addEventListener('click', function(e) {
                        e.preventDefault();
                        if (!this.disabled) {
                            flkty.next();
                        }
                    });
                }
                
                // Update on slide change
                flkty.on('change', updateButtonStates);
                
                // Check on resize
                flkty.on('resize', checkNavigationNeeded);
                
                // Initial state
                checkNavigationNeeded();
                updateButtonStates();
            }
        });
    }

    /**
     * Timeline Slider (Vision/Mission)
     */
    const timelineSliders = document.querySelectorAll('.timeline-slider');
    if (timelineSliders.length > 0 && typeof Flickity !== 'undefined') {
        timelineSliders.forEach(function(slider) {
            // Check if there's a current item (with circle marker)
            const currentItem = slider.querySelector('.timeline-item svg circle');
            const shouldScrollToEnd = !currentItem;
            
            // Initialize Flickity - use groupCells: true, let CSS handle sizing
            const flkty = new Flickity(slider, {
                cellAlign: 'left',
                contain: true,
                prevNextButtons: false,
                pageDots: false,
                groupCells: true,
                draggable: true,
                wrapAround: false,
                initialIndex: 0
            });
            
            // If no current item, scroll to the last slide after initialization
            if (shouldScrollToEnd) {
                // Wait for Flickity to fully initialize
                setTimeout(function() {
                    const lastSlideIndex = flkty.slides.length - 1;
                    flkty.select(lastSlideIndex, false, true); // select(index, isWrapped, isInstant)
                }, 100);
            }

            // Custom navigation buttons
            const navContainer = slider.closest('.timeline-block').querySelector('.timeline-nav');
            if (navContainer) {
                const prevButton = navContainer.querySelector('.timeline-prev');
                const nextButton = navContainer.querySelector('.timeline-next');

                // Function to check if navigation is needed
                function checkNavigationNeeded() {
                    const totalSlides = flkty.slides.length;
                    
                    // Hide navigation if only one slide (all items visible)
                    if (totalSlides <= 1) {
                        navContainer.style.display = 'none';
                    } else {
                        navContainer.style.display = 'flex';
                    }
                }

                // Button state management
                function updateButtonStates() {
                    if (prevButton && nextButton) {
                        // Disable prev button if at start
                        if (flkty.selectedIndex === 0) {
                            prevButton.disabled = true;
                            prevButton.style.opacity = '0.3';
                            prevButton.style.cursor = 'not-allowed';
                        } else {
                            prevButton.disabled = false;
                            prevButton.style.opacity = '1';
                            prevButton.style.cursor = 'pointer';
                        }

                        // Disable next button if at end
                        const lastIndex = flkty.slides.length - 1;
                        if (flkty.selectedIndex >= lastIndex) {
                            nextButton.disabled = true;
                            nextButton.style.opacity = '0.3';
                            nextButton.style.cursor = 'not-allowed';
                        } else {
                            nextButton.disabled = false;
                            nextButton.style.opacity = '1';
                            nextButton.style.cursor = 'pointer';
                        }
                    }
                }

                // Button click handlers
                if (prevButton) {
                    prevButton.addEventListener('click', function(e) {
                        e.preventDefault();
                        if (!this.disabled) {
                            flkty.previous();
                        }
                    });
                }

                if (nextButton) {
                    nextButton.addEventListener('click', function(e) {
                        e.preventDefault();
                        if (!this.disabled) {
                            flkty.next();
                        }
                    });
                }

                // Update on slide change
                flkty.on('change', updateButtonStates);

                // Check on resize
                flkty.on('resize', checkNavigationNeeded);

                // Initial state
                checkNavigationNeeded();
                updateButtonStates();
            }
        });
    }

    // Team Member Bio Toggle (Grid Mode with Filters)
    document.querySelectorAll('.team-member-toggle').forEach(function(toggleBtn) {
        toggleBtn.addEventListener('click', function(e) {
            e.preventDefault();
            const card = this.closest('.team-member-card');
            const bio = card.querySelector('.team-member-bio');
            const photo = card.querySelector('.team-member-photo');
            const plusIcon = this.querySelector('.team-toggle-plus');
            const closeIcon = this.querySelector('.team-toggle-close');

            if (bio && photo) {
                // Toggle visibility
                if (bio.classList.contains('hidden')) {
                    // Close all other open bios
                    document.querySelectorAll('.team-member-bio').forEach(function(otherBio) {
                        if (otherBio !== bio && !otherBio.classList.contains('hidden')) {
                            otherBio.classList.add('hidden');
                            otherBio.closest('.team-member-card').querySelector('.team-member-photo').classList.remove('hidden');
                            const otherToggle = otherBio.closest('.team-member-card').querySelector('.team-member-toggle');
                            if (otherToggle) {
                                otherToggle.querySelector('.team-toggle-plus').classList.remove('hidden');
                                otherToggle.querySelector('.team-toggle-close').classList.add('hidden');
                            }
                        }
                    });

                    // Show bio, hide photo
                    bio.classList.remove('hidden');
                    photo.classList.add('hidden');
                    plusIcon.classList.add('hidden');
                    closeIcon.classList.remove('hidden');
                } else {
                    // Hide bio, show photo
                    bio.classList.add('hidden');
                    photo.classList.remove('hidden');
                    plusIcon.classList.remove('hidden');
                    closeIcon.classList.add('hidden');
                }
            }
        });
    });

    // Team Category Filter (Grid Mode)
    document.querySelectorAll('.team-filter-btn').forEach(function(filterBtn) {
        filterBtn.addEventListener('click', function(e) {
            e.preventDefault();
            const filterValue = this.getAttribute('data-filter');

            // Update active state
            document.querySelectorAll('.team-filter-btn').forEach(function(btn) {
                btn.classList.remove('opacity-100');
                btn.classList.add('opacity-50');
            });
            this.classList.remove('opacity-50');
            this.classList.add('opacity-100');

            // Filter team members
            const teamCards = document.querySelectorAll('.team-member-card');
            teamCards.forEach(function(card) {
                const categories = card.getAttribute('data-category');
                
                // Show all if filter is "all", otherwise filter by category
                if (filterValue === 'all' || !categories || categories.includes(filterValue)) {
                    card.style.display = '';
                } else {
                    card.style.display = 'none';
                }

                // Close any open bios when filtering
                const bio = card.querySelector('.team-member-bio');
                const photo = card.querySelector('.team-member-photo');
                const toggle = card.querySelector('.team-member-toggle');
                if (bio && !bio.classList.contains('hidden')) {
                    bio.classList.add('hidden');
                    photo.classList.remove('hidden');
                    if (toggle) {
                        toggle.querySelector('.team-toggle-plus').classList.remove('hidden');
                        toggle.querySelector('.team-toggle-close').classList.add('hidden');
                    }
                }
            });
        });
    });

    // Initialize first category as active
    const firstFilterBtn = document.querySelector('.team-filter-btn');
    if (firstFilterBtn) {
        firstFilterBtn.classList.add('opacity-100');
        firstFilterBtn.classList.remove('opacity-50');
    }

    // Team Member Image Click/Hover to Show Bio (Grid Mode with Filters Only)
    // Detect if device supports touch
    const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    
    document.querySelectorAll('.team-member-card .team-member-image-wrapper').forEach(function(imageWrapper) {
        const bio = imageWrapper.querySelector('.team-member-bio');
        const photo = imageWrapper.querySelector('.team-member-photo');
        
        if (bio && photo) {
            // Click/Tap event (works for both touch and mouse)
            imageWrapper.addEventListener('click', function(e) {
                e.preventDefault();
                
                // Close all other open bios
                document.querySelectorAll('.team-member-card .team-member-bio').forEach(function(otherBio) {
                    if (otherBio !== bio && !otherBio.classList.contains('hidden')) {
                        otherBio.classList.add('hidden');
                        otherBio.closest('.team-member-card').querySelector('.team-member-photo').classList.remove('hidden');
                    }
                });

                // Toggle this bio
                if (bio.classList.contains('hidden')) {
                    bio.classList.remove('hidden');
                    photo.classList.add('hidden');
                } else {
                    bio.classList.add('hidden');
                    photo.classList.remove('hidden');
                }
            });

            // Hover effect - only on non-touch devices
            if (!isTouchDevice) {
                imageWrapper.addEventListener('mouseenter', function() {
                    bio.classList.remove('hidden');
                    photo.classList.add('hidden');
                });

                imageWrapper.addEventListener('mouseleave', function() {
                    bio.classList.add('hidden');
                    photo.classList.remove('hidden');
                });
            }
        }
    });

    /**
     * Forminator form half-width row handler
     * Adds 'form-row-half-parent' class to parent rows of elements with 'form-row-half' class
     */
    function initFormHalfWidthRows() {
        const formHalfElements = document.querySelectorAll('.form-row-half');
        
        formHalfElements.forEach(function(element) {
            // Find the parent .forminator-row
            const parentRow = element.closest('.forminator-row');
            if (parentRow) {
                parentRow.classList.add('form-row-half-parent');
            }
        });
    }

    // Initialize on page load
    initFormHalfWidthRows();

    // Re-initialize if Forminator loads forms dynamically
    document.addEventListener('forminator:form:submit', function() {
        setTimeout(initFormHalfWidthRows, 100);
    });

    /**
     * Accordion functionality for problem/solution sections
     */
    function initAccordions() {
        const accordionTriggers = document.querySelectorAll('.accordion-trigger');
        
        console.log('Found accordion triggers:', accordionTriggers.length);
        
        accordionTriggers.forEach(function(trigger) {
            // Remove any existing event listeners by cloning
            const newTrigger = trigger.cloneNode(true);
            trigger.parentNode.replaceChild(newTrigger, trigger);
            
            newTrigger.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                console.log('Accordion clicked');
                
                const contentId = this.getAttribute('aria-controls');
                const content = document.getElementById(contentId);
                const isExpanded = this.getAttribute('aria-expanded') === 'true';
                const icon = this.querySelector('.accordion-icon');
                
                console.log('Content ID:', contentId, 'Content found:', !!content, 'Is expanded:', isExpanded);
                
                if (!content) {
                    console.log('Content not found!');
                    return;
                }
                
                if (isExpanded) {
                    // Close
                    console.log('Closing accordion');
                    content.style.maxHeight = '0';
                    this.setAttribute('aria-expanded', 'false');
                    if (icon) {
                        icon.style.transform = 'rotate(0deg)';
                    }
                } else {
                    // Open
                    console.log('Opening accordion');
                    // Get the actual content height
                    const contentInner = content.querySelector('div');
                    const height = contentInner ? contentInner.scrollHeight : content.scrollHeight;
                    console.log('Opening to height:', height);
                    content.style.maxHeight = height + 'px';
                    this.setAttribute('aria-expanded', 'true');
                    if (icon) {
                        icon.style.transform = 'rotate(45deg)';
                    }
                }
            }, false);
        });
    }
    
    // Initialize accordions on page load
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAccordions);
    } else {
        initAccordions();
    }

    /**
     * Timeline Horizontal Scroll Hijacking
     * Converts vertical scroll to horizontal scroll when timeline is centered in viewport
     */
    const timelineContainer = document.querySelector('.timeline-scroll-container');
    
    if (timelineContainer) {
        let isScrollingTimeline = false;
        
        function handleWheel(e) {
            const rect = timelineContainer.getBoundingClientRect();
            const viewportCenter = window.innerHeight / 2;
            const elementCenter = rect.top + (rect.height / 2);
            
            // Check if element center is near viewport center (within 20% of viewport height)
            const threshold = window.innerHeight * 0.2;
            const isCentered = Math.abs(elementCenter - viewportCenter) < threshold;
            
            if (!isCentered) {
                isScrollingTimeline = false;
                return;
            }
            
            const scrollLeft = timelineContainer.scrollLeft;
            const maxScrollLeft = timelineContainer.scrollWidth - timelineContainer.clientWidth;
            
            // Check if we're scrolling down
            if (e.deltaY > 0) {
                // Scrolling down
                if (scrollLeft < maxScrollLeft) {
                    // Timeline hasn't reached the end, hijack scroll
                    e.preventDefault();
                    timelineContainer.scrollLeft += e.deltaY;
                    isScrollingTimeline = true;
                } else {
                    // Timeline reached the end, allow normal scroll
                    isScrollingTimeline = false;
                }
            } else {
                // Scrolling up
                if (scrollLeft > 0) {
                    // Timeline hasn't reached the start, hijack scroll
                    e.preventDefault();
                    timelineContainer.scrollLeft += e.deltaY;
                    isScrollingTimeline = true;
                } else {
                    // Timeline reached the start, allow normal scroll
                    isScrollingTimeline = false;
                }
            }
        }
        
        // Add wheel event listener with passive: false to allow preventDefault
        window.addEventListener('wheel', handleWheel, { passive: false });
    }

    /**
     * Add borders between sections with the same background color
     */
    function addBordersBetweenSameBgSections() {
        // Get all main content sections (flexible content blocks)
        const sections = document.querySelectorAll('section[class*="-block"]');
        
        sections.forEach((section, index) => {
            if (index === 0) return; // Skip first section
            
            const currentBg = window.getComputedStyle(section).backgroundColor;
            const prevSection = sections[index - 1];
            const prevBg = window.getComputedStyle(prevSection).backgroundColor;
            
            // Remove any existing border divs
            const existingBorder = section.querySelector('.section-border-top');
            if (existingBorder) {
                existingBorder.remove();
            }
            
            // If backgrounds match, add border div with content padding
            if (currentBg === prevBg && currentBg !== 'rgba(0, 0, 0, 0)' && currentBg !== 'transparent') {
                const borderDiv = document.createElement('div');
                borderDiv.className = 'section-border-top px-4 sm:px-6 md:px-12 lg:px-16';
                borderDiv.style.cssText = 'position: absolute; top: 0; left: 0; right: 0; pointer-events: none;';
                
                const borderLine = document.createElement('div');
                borderLine.style.cssText = 'border-top: 1px solid rgba(0, 0, 0, 0.1); width: 100%;';
                
                borderDiv.appendChild(borderLine);
                
                // Make sure section is positioned relatively
                if (window.getComputedStyle(section).position === 'static') {
                    section.style.position = 'relative';
                }
                
                section.insertBefore(borderDiv, section.firstChild);
            }
        });
    }

    // Run on page load
    addBordersBetweenSameBgSections();

    // Re-run on window resize (in case backgrounds change responsively)
    let borderResizeTimer;
    window.addEventListener('resize', function() {
        clearTimeout(borderResizeTimer);
        borderResizeTimer = setTimeout(function() {
            addBordersBetweenSameBgSections();
        }, 250);
    });

})();
